import { useState } from 'react';

interface Tab {
  id: string;
  label: string;
  title: string;
  desc: string;
  features: string[];
  stats: { number: string; label: string }[];
  visual: {
    abbr: string;
    abbrColor: string;
    abbrBg: string;
    company: string;
    subtitle: string;
    steps: { icon: string; iconColor: string; iconBg: string; text: string; img?: string }[];
  };
}

const tabs: Tab[] = [
  {
    id: 'accountants',
    label: 'Účetní kanceláře',
    title: '40 klientů, jeden tým.\nBez přesčasů.',
    desc: 'Spravujete desítky firem a každá má jiný e-shop, jinou platební bránu, jiný formát dat. Ráno otevřete e-mail se třemi CSV soubory, dvěma excely a jedním „posílám faktury v příloze". SyncroX tohle všechno zpracuje za vás — data jsou v Money S3 dřív, než dorazíte do kanceláře.',
    features: [
      'Multi-tenant: každý klient má vlastní konfiguraci a mapování',
      'CSV/XLSX/XML Mapper s AI rozpoznáváním — importujte cokoliv',
      'Automatické předkontace podle typu dokladu (poplatky, tržby, vratky)',
      'Přepínání mezi firmami jedním kliknutím v portálu',
    ],
    stats: [
      { number: '150+ h', label: 'ušetřeno měsíčně' },
      { number: '40+', label: 'klientů na 1 tým' },
      { number: '0', label: 'chyb v přepisu' },
    ],
    visual: {
      abbr: 'SA', abbrColor: 'var(--accent)', abbrBg: 'var(--accent-soft)',
      company: 'SANETO spol. s r.o.', subtitle: '12 klientů · 3 200 dokladů/měsíc',
      steps: [
        { icon: 'Sh', iconColor: '#16A34A', iconBg: '#16A34A18', text: 'Shoptet — objednávky (klient: Stedis)', img: 'shoptet_pay.jpeg' },
        { icon: 'S', iconColor: '#635BFF', iconBg: '#635BFF18', text: 'Stripe — platby (klient: Prague Hotel)', img: 'stripe.webp' },
        { icon: 'Z', iconColor: '#DC2626', iconBg: '#DC262618', text: 'Zásilkovna — CSV export (klient: Aalbatros)', img: 'Zasilkovna.png' },
        { icon: 'AI', iconColor: '#7C3AED', iconBg: '#7C3AED18', text: 'XLSX import — AI Mapper rozpozná strukturu' },
      ],
    },
  },
  {
    id: 'eshops',
    label: 'E-shopy',
    title: 'Objednávka v e-shopu?\nZa 5 minut je v Money S3.',
    desc: 'Prodáváte přes Shoptet, WooCommerce nebo Upgates. Platby chodí přes Stripe a Comgate. Zásilky jdou přes Zásilkovnu a DPD. Každý den 50–500 objednávek. Ručně byste to přepisovali do Money S3 celý den. Se SyncroX se o tom ani nedozvíte — prostě to tam je.',
    features: [
      'Objednávky → faktury automaticky se správným DPH a položkami',
      'Platby z brány automaticky spárované s fakturami',
      'Zásilky a dobírky zaúčtované podle dopravce',
      'Vratky a storna zpracované bez manuálního zásahu',
    ],
    stats: [
      { number: '20+ h', label: 'ušetřeno týdně' },
      { number: '5 min', label: 'objednávka → faktura' },
      { number: '99.8%', label: 'přesnost dat' },
    ],
    visual: {
      abbr: 'ST', abbrColor: '#16A34A', abbrBg: '#16A34A18',
      company: 'Stedis', subtitle: '~120 objednávek/den · Shoptet + Stripe',
      steps: [
        { icon: 'Sh', iconColor: '#16A34A', iconBg: '#16A34A18', text: 'Shoptet — nová objednávka #18472', img: 'shoptet_pay.jpeg' },
        { icon: 'S', iconColor: '#635BFF', iconBg: '#635BFF18', text: 'Stripe — platba 1 250 Kč přijata', img: 'stripe.webp' },
        { icon: 'Z', iconColor: '#DC2626', iconBg: '#DC262618', text: 'Zásilkovna — zásilka odeslána', img: 'Zasilkovna.png' },
      ],
    },
  },
  {
    id: 'hotels',
    label: 'Hotely',
    title: 'Rezervace z Mews v Money S3.\nAutomaticky. Správně.',
    desc: 'Hotelový provoz = stovky transakcí denně. Platby kartou, hotově, zálohy, doplňkové služby, storna. Každá s jiným účetním případem. Váš recepční to přepisuje po nocích. SyncroX to udělá okamžitě — a správně zaúčtuje i ten minibar ze čtvrtého patra.',
    features: [
      'Mews a Previo integrace — rezervace, platby, doplňkové služby',
      'Automatické rozlišení: karta, hotovost, záloha, dobropisy',
      'FAPI integrace pro doplňkové prodeje a vstupenky',
      'Měsíční uzávěrka bez stresu a bez přesčasů',
    ],
    stats: [
      { number: '30+ h', label: 'ušetřeno měsíčně' },
      { number: '0', label: 'chyb v přepisu' },
      { number: '1 den', label: 'uzávěrka místo 5' },
    ],
    visual: {
      abbr: 'PH', abbrColor: '#2563EB', abbrBg: '#2563EB18',
      company: 'Prague Hotel', subtitle: '86 pokojů · Mews PMS + FAPI',
      steps: [
        { icon: 'M', iconColor: '#2563EB', iconBg: '#2563EB18', text: 'Mews — check-out pokoj 412 (3 noci + minibar)', img: 'mews.webp' },
        { icon: 'M', iconColor: '#2563EB', iconBg: '#2563EB18', text: 'Mews — platba kartou 8 940 Kč', img: 'mews.webp' },
        { icon: 'F', iconColor: '#EA580C', iconBg: '#EA580C18', text: 'FAPI — vstupenka wellness 490 Kč (hotovost)', img: 'fapi.png' },
      ],
    },
  },
  {
    id: 'manufacturing',
    label: 'Výroba & sklad',
    title: 'Příjemky, výdejky, faktury.\nBez ručního přepisu.',
    desc: 'Výrobní firmy a sklady pracují s desítkami dodavatelů. Každý posílá faktury a dodací listy v jiném formátu — PDF, Excel, XML, e-mail. Někdo i poštou. Váš tým to ručně zadává do Money S3. Se SyncroX nasajeme data z čehokoliv díky AI Mapperu a převedeme do správného formátu.',
    features: [
      'AI Mapper pro CSV, XLSX, XML — rozpozná strukturu automaticky',
      'Automatický import přijatých faktur s předkontací',
      'Napojení na skladové systémy a ERP',
      'Vlastní integrace na míru do 2 týdnů',
    ],
    stats: [
      { number: '25+ h', label: 'ušetřeno měsíčně' },
      { number: '2 týdny', label: 'custom integrace' },
      { number: '100%', label: 'přizpůsobitelné' },
    ],
    visual: {
      abbr: 'AL', abbrColor: '#B8860B', abbrBg: '#B8860B18',
      company: 'Aalbatros', subtitle: 'Výroba · 14 dodavatelů · ~400 faktur/měsíc',
      steps: [
        { icon: 'XL', iconColor: '#B8860B', iconBg: '#B8860B18', text: 'Dodavatel A — XLSX s 45 položkami' },
        { icon: 'AI', iconColor: '#7C3AED', iconBg: '#7C3AED18', text: 'AI Mapper — rozpozná strukturu, namapuje pole' },
        { icon: 'XML', iconColor: '#DC2626', iconBg: '#DC262618', text: 'Dodavatel B — XML faktury (ISDOC)' },
      ],
    },
  },
];

export default function SolutionsTabs() {
  const [activeTab, setActiveTab] = useState('accountants');
  const tab = tabs.find(t => t.id === activeTab)!;

  return (
    <section className="tabs-section" id="solutions">
      <div className="container">
        <div className="section-header">
          <span className="section-label">Pro koho je SyncroX</span>
          <h2>Řešení pro váš obor</h2>
          <p>Každé odvětví má jiné systémy, jiné bolesti a jiné potřeby. Proto děláme řešení na míru.</p>
        </div>

        <div className="tabs-nav">
          {tabs.map(t => (
            <button key={t.id} className={`tab-btn${t.id === activeTab ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>

        <div className="tab-content">
          <div className="tab-text">
            <h3 style={{ whiteSpace: 'pre-line' }}>{tab.title}</h3>
            <p>{tab.desc}</p>
            <div className="tab-features">
              {tab.features.map((f, i) => (
                <div key={i} className="tab-feature"><span className="check">&#10003;</span>{f}</div>
              ))}
            </div>
            <div className="tab-stats">
              {tab.stats.map((s, i) => (
                <div key={i}><div className="tab-stat-number">{s.number}</div><div className="tab-stat-label">{s.label}</div></div>
              ))}
            </div>
          </div>

          <div className="tab-visual">
            <div className="tab-visual-header">
              <div className="tab-visual-icon" style={{ background: tab.visual.abbrBg, color: tab.visual.abbrColor }}>{tab.visual.abbr}</div>
              <div>
                <div className="tab-visual-title">{tab.visual.company}</div>
                <div className="tab-visual-subtitle">{tab.visual.subtitle}</div>
              </div>
            </div>
            <div className="flow-diagram">
              {tab.visual.steps.map((step, i) => (
                <div key={i} className="flow-step">
                  {step.img ? (
                    <img className="flow-step-img" src={`/integrations/${step.img}`} alt="" />
                  ) : (
                    <div className="flow-step-icon" style={{ background: step.iconBg, color: step.iconColor }}>{step.icon}</div>
                  )}
                  {step.text}
                </div>
              ))}
              <div className="flow-arrow">&darr;</div>
              <div className="flow-step" style={{ borderColor: 'var(--accent)', background: 'var(--accent-soft)' }}>
                <img className="flow-step-img" src="/integrations/moneyS3.webp" alt="Money S3" />
                <strong>Money S3</strong> — doklady se správnými předkontacemi
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
